import { useMemo } from 'react';
import {
  Button,
  TableCell,
  TableRow,
  Tag,
  Tile,
  InlineNotification,
} from '@carbon/react';
import {
  ChevronDown,
  ChevronRight,
  CheckmarkFilled,
  CloseFilled,
} from '@carbon/react/icons';
import { ShapeCombinationRow } from './shape-combination-row';
import { formatNumber } from 'utils/number';
import { GeometryData } from './types';
import { OWNERSHIP_DECIMAL_PLACES } from './constants';
import style from './reconciler.module.scss';

const compareLeasesByName = (
  a: { leaseName: string; executiveOwnerName?: string | null },
  b: { leaseName: string; executiveOwnerName?: string | null }
) => {
  const leaseCompare = a.leaseName.localeCompare(b.leaseName);
  if (leaseCompare !== 0) return leaseCompare;
  return (a.executiveOwnerName || '').localeCompare(b.executiveOwnerName || '');
};

type GeometryRowProps = {
  geometry: GeometryData;
  expandedGeometries: Set<string>;
  toggleGeometry: (geometryName: string) => void;
  workspaces: Record<number, { name: string; lastCalculated: string | null }>;
};

export const GeometryRow = ({
  geometry,
  expandedGeometries,
  toggleGeometry,
  workspaces,
}: GeometryRowProps) => {
  const isExpanded = expandedGeometries.has(geometry.geometryName);

  const uniqueInterestTypeCount = useMemo(() => {
    const allInterestTypes = geometry.shapes.flatMap((shape) =>
      shape.interestComparisons.map((c) => c.interestType)
    );
    return new Set(allInterestTypes).size;
  }, [geometry.shapes]);

  const sortedMissingInEntityTitleAssets = useMemo(
    () =>
      [
        ...(geometry.burdenedLeaseComparison?.missingInEntityTitleAssets ?? []),
      ].sort(compareLeasesByName),
    [geometry.burdenedLeaseComparison?.missingInEntityTitleAssets]
  );

  const sortedMissingInValidator = useMemo(
    () =>
      [...(geometry.burdenedLeaseComparison?.missingInValidator ?? [])].sort(
        compareLeasesByName
      ),
    [geometry.burdenedLeaseComparison?.missingInValidator]
  );

  return (
    <>
      {/* Summary row */}
      <TableRow>
        <TableCell>
          <div className={style.geometryNameRow}>
            <Button
              hasIconOnly
              kind="ghost"
              size="sm"
              renderIcon={isExpanded ? ChevronDown : ChevronRight}
              iconDescription="Expand"
              onClick={() => toggleGeometry(geometry.geometryName)}
            />
            <strong>{geometry.geometryName}</strong>
          </div>
        </TableCell>
        <TableCell>
          {geometry.shapes.length}{' '}
          {geometry.shapes.length === 1 ? 'shape' : 'shapes'}
        </TableCell>
        <TableCell>
          <div className={style.statusStacked}>
            {geometry.shapeMatched > 0 && (
              <span className={style.statusIndicator}>
                <CheckmarkFilled className={style.validIcon} />
                {geometry.shapeMatched} match
              </span>
            )}
            {geometry.shapeFailed > 0 && (
              <span className={style.statusIndicator}>
                <CloseFilled className={style.invalidIcon} />
                {geometry.shapeFailed} mismatch
              </span>
            )}
          </div>
        </TableCell>
        <TableCell>
          {uniqueInterestTypeCount}{' '}
          {uniqueInterestTypeCount === 1 ? 'type' : 'types'}
        </TableCell>
        <TableCell colSpan={2} />
        <TableCell>
          <div className={style.statusStacked}>
            {geometry.interestMatched > 0 && (
              <span className={style.statusIndicator}>
                <CheckmarkFilled className={style.validIcon} />
                {geometry.interestMatched} match
              </span>
            )}
            {geometry.interestFailed > 0 && (
              <span className={style.statusIndicator}>
                <CloseFilled className={style.invalidIcon} />
                {geometry.interestFailed} mismatch
              </span>
            )}
          </div>
        </TableCell>
      </TableRow>
      {/* Expanded shape combinations */}
      {isExpanded &&
        geometry.shapes.map((shape) => (
          <ShapeCombinationRow
            key={shape.shapeIds.join('+')}
            shape={shape}
            geometryName={geometry.geometryName}
            workspaces={workspaces}
          />
        ))}
      {/* Burdened Lease Comparison */}
      {isExpanded && geometry.burdenedLeaseComparison && (
        <TableRow>
          <TableCell colSpan={7}>
            <Tile className={style.burdenedLeaseTile}>
              <h4 className={style.burdenedLeaseHeader}>
                Burdened Leases for {geometry.geometryName}
              </h4>

              <div className={style.burdenedLeaseSummary}>
                <span>
                  Validator:{' '}
                  {geometry.burdenedLeaseComparison.validatorLeaseCount} leases
                </span>
                <span>
                  Entity: {geometry.burdenedLeaseComparison.entityLeaseCount}{' '}
                  leases
                </span>
                <span>
                  Matched:{' '}
                  {geometry.burdenedLeaseComparison.matchedLeases.length}
                </span>
                {geometry.burdenedLeaseComparison.match ? (
                  <Tag type="green" size="sm">
                    All Match
                  </Tag>
                ) : (
                  <Tag type="red" size="sm">
                    Has Discrepancies
                  </Tag>
                )}
              </div>

              {geometry.burdenedLeaseComparison.matchedLeases.length > 0 && (
                <InlineNotification
                  kind="success"
                  lowContrast
                  hideCloseButton
                  title="Matched Leases"
                  subtitle={
                    (
                      <ul className={style.leaseList}>
                        {geometry.burdenedLeaseComparison.matchedLeases.map(
                          (lease) => (
                            <li
                              key={`${lease.documentId}-${lease.leaseName}-${lease.executiveOwnerName}`}
                            >
                              <strong>{lease.leaseName}</strong> -{' '}
                              {lease.executiveOwnerName}
                              {lease.executiveAmount !== null &&
                                ` (${formatNumber(
                                  lease.executiveAmount,
                                  OWNERSHIP_DECIMAL_PLACES,
                                  OWNERSHIP_DECIMAL_PLACES,
                                  'decimal',
                                  false
                                )})`}
                            </li>
                          )
                        )}
                      </ul>
                    ) as unknown as string
                  }
                  className={style.notificationSpacing}
                />
              )}

              {sortedMissingInEntityTitleAssets.length > 0 && (
                <InlineNotification
                  kind="error"
                  lowContrast
                  hideCloseButton
                  title="Missing in Entity Title Assets"
                  subtitle={
                    (
                      <ul className={style.leaseList}>
                        {sortedMissingInEntityTitleAssets.map((lease) => (
                          <li
                            key={`${lease.documentId}-${lease.leaseName}-${lease.executiveOwnerName}`}
                          >
                            <strong>{lease.leaseName}</strong> -{' '}
                            {lease.executiveOwnerName}
                            {lease.executiveAmount !== null &&
                              ` (${formatNumber(
                                lease.executiveAmount,
                                OWNERSHIP_DECIMAL_PLACES,
                                OWNERSHIP_DECIMAL_PLACES,
                                'decimal',
                                false
                              )})`}
                          </li>
                        ))}
                      </ul>
                    ) as unknown as string
                  }
                  className={style.notificationSpacing}
                />
              )}

              {sortedMissingInValidator.length > 0 && (
                <InlineNotification
                  kind="error"
                  lowContrast
                  hideCloseButton
                  title="Missing in Validator"
                  subtitle={
                    (
                      <ul className={style.leaseList}>
                        {sortedMissingInValidator.map((lease) => (
                          <li
                            key={`${lease.documentId}-${lease.leaseName}-${lease.executiveOwnerName}`}
                          >
                            <strong>{lease.leaseName}</strong> (
                            {lease.leaseType}) - {lease.executiveOwnerName} (
                            {lease.royaltyAmount})
                          </li>
                        ))}
                      </ul>
                    ) as unknown as string
                  }
                  className={style.notificationSpacing}
                />
              )}

              {geometry.burdenedLeaseComparison.calculationMismatches.length >
                0 && (
                <InlineNotification
                  kind="error"
                  lowContrast
                  hideCloseButton
                  title="Calculation Mismatches"
                  subtitle={
                    (
                      <ul className={style.leaseList}>
                        {geometry.burdenedLeaseComparison.calculationMismatches.map(
                          (mismatch) => (
                            <li
                              key={`${mismatch.documentId}-${mismatch.leaseName}-${mismatch.executiveOwner}`}
                            >
                              <strong>{mismatch.leaseName}</strong> -{' '}
                              {mismatch.executiveOwner}:
                              <br />
                              <strong>Royalty Amount:</strong>
                              <br />
                              &nbsp;&nbsp;Validator:{' '}
                              {mismatch.interestType === 'executive'
                                ? mismatch.royaltyAmount.validatorFormula
                                : `${mismatch.royaltyAmount.validatorFormula} = ${mismatch.royaltyAmount.validatorCalculatedAmount}`}
                              <br />
                              &nbsp;&nbsp;Entity:{' '}
                              {mismatch.royaltyAmount.entityRoyaltyAmount}
                              <br />
                              &nbsp;&nbsp;Δ {mismatch.royaltyAmount.difference}
                              <br />
                              <strong>NRI:</strong>
                              <br />
                              &nbsp;&nbsp;Validator: {mismatch.nri.validatorNri}
                              <br />
                              &nbsp;&nbsp;Entity: {mismatch.nri.entityNri}
                              <br />
                              &nbsp;&nbsp;Δ {mismatch.nri.difference}
                            </li>
                          )
                        )}
                      </ul>
                    ) as unknown as string
                  }
                  className={style.notificationSpacing}
                />
              )}

              {geometry.burdenedLeaseComparison.match && (
                <InlineNotification
                  kind="success"
                  lowContrast
                  hideCloseButton
                  title="All burdened leases match between validator and entity title assets"
                />
              )}
            </Tile>
          </TableCell>
        </TableRow>
      )}
    </>
  );
};
